import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import ProviderIcon from "@/components/ProviderIcon";
import S3AccountsPanel from "@/components/S3AccountsPanel";
import { Plus, Trash2, Mail, KeyRound, ShieldCheck, Database, FolderTree } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Accounts() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get("welcome") === "1";
  const initialTab = searchParams.get("tab") === "s3" ? "s3" : "drive";
  const [tab, setTab] = useState(initialTab);
  const [accounts, setAccounts] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [providersStatus, setProvidersStatus] = useState([]);

  const load = async () => {
    const [a, p] = await Promise.all([
      api.get("/drive/accounts"),
      api.get("/providers"),
    ]);
    setAccounts(a.data);
    setProvidersStatus(p.data.providers);
  };

  useEffect(() => {
    load();
  }, []);

  // Handle Microsoft admin consent return (?ms_admin_consent=ok|error)
  useEffect(() => {
    const status = searchParams.get("ms_admin_consent");
    if (status === "ok") {
      const tenant = searchParams.get("tenant");
      toast.success(
        tenant
          ? t("accountsPage.msAdminConsentOk").replace("{tenant}", tenant)
          : t("accountsPage.msAdminConsentOk").replace("{tenant}", "")
      );
    } else if (status === "error") {
      const reason = searchParams.get("reason") || "";
      toast.error(`${t("accountsPage.msAdminConsentError")} ${reason}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async (providerName) => {
    const ps = providersStatus.find((p) => p.name === providerName);
    if (!ps?.configured) {
      toast.error(`${ps?.label || providerName} OAuth not configured — add credentials in backend .env`);
      return;
    }
    try {
      const { data } = await api.get("/drive/accounts/connect-url", { params: { provider: providerName } });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleMsAdminConsent = async () => {
    try {
      const { data } = await api.get("/auth/microsoft/admin-consent-url");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleRemove = async (id) => {
    try {
      await api.delete(`/drive/accounts/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setConfirm(null);
    }
  };

  const anyConfigured = providersStatus.some((p) => p.configured);

  return (
    <div className="App min-h-screen bg-background">
      <Header variant="app" />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Cloud accounts</div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              {t("accountsPage.title")}
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("accountsPage.subtitle")}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className={`rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2 ${tab !== "drive" ? "hidden" : ""}`}
                data-testid="connect-account-button"
              >
                <Plus className="h-4 w-4" /> {t("accountsPage.connect")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm w-72">
              <DropdownMenuItem onClick={() => handleConnect("google")} data-testid="connect-google">
                <ProviderIcon provider="google" className="mr-2 h-4 w-4" />
                {t("accountsPage.connectGoogle")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleConnect("microsoft")} data-testid="connect-microsoft">
                <ProviderIcon provider="microsoft" className="mr-2 h-4 w-4" />
                {t("accountsPage.connectMicrosoft")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleConnect("dropbox")} data-testid="connect-dropbox">
                <ProviderIcon provider="dropbox" className="mr-2 h-4 w-4" />
                {t("accountsPage.connectDropbox")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("accountsPage.msAdminLabel")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={handleMsAdminConsent}
                data-testid="connect-microsoft-admin-consent"
                className="flex-col items-start gap-0.5"
              >
                <div className="flex items-center">
                  <ShieldCheck className="mr-2 h-4 w-4 text-[#002FA7]" />
                  <span className="font-medium">{t("accountsPage.msAdminConsent")}</span>
                </div>
                <span className="ml-6 text-[11px] leading-tight text-muted-foreground">
                  {t("accountsPage.msAdminConsentHint")}
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Tabs : Drive vs S3 */}
        <div className="mt-6 flex border-b border-border">
          <button
            type="button"
            onClick={() => setTab("drive")}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
              tab === "drive"
                ? "border-[#002FA7] text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-drive"
          >
            <FolderTree className="h-4 w-4" />
            {t("accountsPage.tabDrive")}
          </button>
          <button
            type="button"
            onClick={() => setTab("s3")}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 font-mono text-xs uppercase tracking-wider transition-colors ${
              tab === "s3"
                ? "border-[#FF3D00] text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid="tab-s3"
          >
            <Database className="h-4 w-4" />
            {t("accountsPage.tabS3")}
          </button>
        </div>

        {isWelcome && (
          <div className="mt-8 border border-[#002FA7]/40 bg-[#002FA7]/5 p-4" data-testid="accounts-welcome-banner">
            <div className="flex items-center gap-2 font-mono text-xs uppercase text-[#002FA7]">
              <Plus className="h-4 w-4" /> {t("accountsPage.welcomeTitle")}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("accountsPage.welcomeBody")}
            </p>
          </div>
        )}

        {providersStatus.length > 0 && !anyConfigured && (
          <div className="mt-8 border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
            <div className="flex items-center gap-2 font-mono text-xs uppercase text-amber-700 dark:text-amber-400">
              <KeyRound className="h-4 w-4" /> OAuth pending
            </div>
            <p className="mt-2 text-muted-foreground">
              Add OAuth credentials in <code className="font-mono">/app/backend/.env</code> for the providers you want
              to enable (<code className="font-mono">GOOGLE_CLIENT_ID</code> /{" "}
              <code className="font-mono">GOOGLE_CLIENT_SECRET</code> for Drive,{" "}
              <code className="font-mono">MS_CLIENT_ID</code> / <code className="font-mono">MS_CLIENT_SECRET</code> for
              OneDrive), then restart the backend. In demo mode, mock accounts are provided so you can configure backups.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 font-mono text-[11px]">
              {providersStatus.map((p) => (
                <span key={p.name} className={`flex items-center gap-1 border px-2 py-0.5 ${p.configured ? "border-emerald-500 text-emerald-700" : "border-border text-muted-foreground"}`}>
                  <ProviderIcon provider={p.name} className="h-3 w-3" />
                  {p.label} · {p.configured ? "ready" : "pending"}
                </span>
              ))}
            </div>
          </div>
        )}

        {tab === "drive" && (
        <section className="mt-8">
          {accounts === null && (
            <div className="font-mono text-sm text-muted-foreground">{t("common.loading")}</div>
          )}
          {accounts && accounts.length === 0 && (
            <div className="border border-dashed border-border bg-card p-10 text-center">
              <Mail className="mx-auto h-8 w-8 text-muted-foreground" />
              <h3 className="mt-4 font-display text-lg font-bold">{t("accountsPage.noAccounts")}</h3>
            </div>
          )}
          {accounts && accounts.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {accounts.map((a) => (
                <div key={a.id} className="flex items-center gap-4 border border-border bg-card p-4">
                  <div className="relative">
                    {a.google_picture ? (
                      <img src={a.google_picture} alt="" className="h-10 w-10 rounded-sm" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center bg-[#002FA7] text-white font-mono">
                        {a.google_email[0].toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-background ring-2 ring-card">
                      <ProviderIcon provider={a.provider} className="h-3 w-3" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate font-display font-bold tracking-tight">{a.google_name || a.google_email}</div>
                      {a.is_primary && (
                        <Badge className="rounded-sm bg-[#002FA7] text-white font-mono text-[10px]">
                          {t("accountsPage.primary")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="rounded-sm font-mono text-[10px] uppercase">
                        {a.provider === "microsoft"
                          ? t("accountsPage.providerMicrosoft")
                          : a.provider === "dropbox"
                          ? t("accountsPage.providerDropbox")
                          : t("accountsPage.providerGoogle")}
                      </Badge>
                      <div className="truncate font-mono text-xs text-muted-foreground">{a.google_email}</div>
                    </div>
                  </div>
                  {!a.is_primary ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-sm"
                      onClick={() => setConfirm(a)}
                      data-testid={`remove-account-${a.id}`}
                      title={t("accountsPage.remove")}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
        )}

        {tab === "s3" && (
          <section className="mt-8">
            <S3AccountsPanel />
          </section>
        )}
      </main>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("accountsPage.remove")}</AlertDialogTitle>
            <AlertDialogDescription>{t("accountsPage.confirmRemove")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-remove-account">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleRemove(confirm.id)}
              data-testid="confirm-remove-account"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
